/**
 * Nav registry — the item definitions and the role/feature gates that
 * decide which of them a given user may see.
 *
 * Split out of NavSidebar so Settings → Navigation renders the SAME gated
 * tree the rail does. Two copies of this list is how a settings panel ends
 * up offering to un-hide a page the user can't route to.
 *
 * Gating and preferences are strictly ordered: gates run HERE, sidebar
 * preferences (@techstack/core `applyNavPrefs`) run after and may only
 * subtract. Hiding a page is a UX choice; it is never access control.
 */

import {
  Home, Layers, GitPullRequest, Database, ClipboardCheck,
  Shield, Radio,
  FlaskConical, Crown, Flag, BookOpen, Activity, AlertTriangle, BarChart3,
  FileText, Globe, Zap, ListChecks, Compass, Sparkles, Briefcase, Eye, Brain,
  Network, Sun, Mic, ReceiptText, ListTodo, Newspaper,
  // Atlas sub-nav icons
  Map as MapIcon, Building2, Palette, Box, Package, Users, Play, GitBranch, GraduationCap,
  Gamepad2, PieChart, Wrench, Target, FileOutput, MessageSquare, Table2,
} from 'lucide-react';

// Items may have a `children` array — those become an expandable group.
// `defaultChild` defines the route clicked when the parent itself is hit
// in icons-only mode (where children aren't visible).

// Base nav items - will be reordered based on role.
//
// GAS-shell parity (docs/SETTINGS-UX-REVIEW-2026-08.md §9, P1): the top
// of the rail follows the SAME order as the GAS dashboard's SECTIONS
// list — Execution · Accounts · Tasks · Contacts · Opportunities ·
// Vendors · Deals · Tools — mapped to TekStak's routes. The shared
// order/labels/blurbs live in @techstack/core (APP_SECTIONS);
// Capture/Review stay in-tenant (trust boundary) and Strategies/
// Presentations have no standalone TekStak surface yet. TekStak-only
// extras follow below the parity block.
export const NAV_ITEMS_BASE = [
  // ── GAS-parity core (same order as the GAS rail) ──────────────────
  { id: 'command-center', label: 'Execution', icon: Zap, adminOnly: true },
  { id: 'accounts', label: 'Accounts', icon: Briefcase, adminOnly: true },
  // Tasks — GTD TODO Funnel canvas (docs/TODO-FUNNEL-VISION.md)
  { id: 'tasks', label: 'Tasks', icon: ListTodo, adminOnly: true },
  // Contacts — GAS-parity standalone page (P7); deep org-chart work
  // keeps its own Org Chart entry below.
  { id: 'contacts', label: 'Contacts', icon: Users, adminOnly: true },
  { id: 'opportunities', label: 'Opportunities', icon: Target, adminOnly: true },
  { id: 'vendors', label: 'Vendors', icon: Crown },
  // Quote Builder (Phase 4) — shared component with the GAS app; same
  // quotes via the JWT→API-key ownership bridge. Admin-only until field-tested.
  { id: 'quotes', label: 'Deals', icon: ReceiptText, adminOnly: true },

  // ── TekStak extras (everything GAS doesn't have) ──────────────────
  // Sales AI Canvas — personal sales brain.
  { id: 'sales-ai', label: 'Sales AI', icon: Sparkles, primary: true, adminOnly: true },
  // Daily Digest — morning briefing with proactive intelligence
  { id: 'digest', label: 'Daily Digest', icon: Sun, adminOnly: true },
  // News — the persisted, department- and contact-tagged front page.
  // Gated by the news_feed feature toggle (Settings → Features), not a role.
  { id: 'news', label: 'News', icon: Newspaper },
  // Org Chart — dedicated entry for customer org chart visualization
  { id: 'workspace?view=chart', label: 'Org Chart', icon: Network, adminOnly: true },
  // Recordings — Plaud recordings viewer with Work/Personal categorization
  { id: 'recordings', label: 'Recordings', icon: Mic, adminOnly: true },
  { id: 'welcome', label: 'Get Started', icon: Sparkles, paloOnly: true },
  // Analysis — the public-data analysis views grouped under one entry
  // (wave 3, Ryan-approved): the rail keeps the daily sales drivers
  // top-level and the analysis library one hover away. Each child keeps
  // its own FEATURE_MAP gate; palo users still get a flat rail via
  // PALO_NAV_ORDER (getNavItems flattens groups for that lookup).
  {
    id: 'analysis',
    label: 'Analysis',
    icon: BarChart3,
    defaultChild: 'home',
    children: [
      { id: 'home', label: 'Home', icon: Home },
      { id: 'account-lists', label: 'My Lists', icon: ListChecks },
      { id: 'tech-stack', label: 'Tech Stack', icon: Layers },
      { id: 'sankey', label: 'Spending Flow', icon: GitPullRequest },
      { id: 'classification', label: 'Classification', icon: ClipboardCheck },
      { id: 'solicitations', label: 'Solicitations', icon: FileText },
      { id: 'zero-trust', label: 'Zero Trust', icon: Shield },
      // The top-level GAS-parity "Vendors" entry now points at /vendors;
      // the full Vendor Competitive Analysis workbench lives here.
      { id: 'vendor-analysis', label: 'Vendor analysis', icon: Crown },
    ],
  },
  // Tools — the GAS-parity slot for working destinations: operational
  // dashboards + data workbenches (was labeled "Observability").
  {
    id: 'observability',
    label: 'Tools',
    icon: Eye,
    defaultChild: 'data',
    children: [
      { id: 'data',                    label: 'Data Gaps',       icon: Database },
      { id: 'backlog-buster',          label: 'Backlog Buster',  icon: Zap },
      { id: 'classification/health',   label: 'Cls Health',      icon: Activity,       adminOnly: true },
      // No badgeKey on triage: its count (suspect signals) is unbounded —
      // a permanently-maxed badge carries no information. Flags/rules are
      // bounded operator queues, so their badges stay.
      { id: 'classification/triage',   label: 'Cls Triage',      icon: AlertTriangle,  adminOnly: true },
      { id: 'classification/flags',    label: 'Cls Flags',       icon: Flag,           adminOnly: true, badgeKey: 'flags' },
      { id: 'classification/rules',    label: 'Rule Candidates', icon: BookOpen,       adminOnly: true, badgeKey: 'rules' },
      { id: 'scraper',                 label: 'Scraper Ops',     icon: Radio,          adminOnly: true },
      { id: 'memory',                  label: 'Memory',          icon: Brain,          adminOnly: true },
      // Data workbenches — moved out of Settings (2026-08 redesign):
      // settings hold preferences; these are working destinations.
      { id: 'classification-hub',      label: 'Classification Hub', icon: ClipboardCheck },
      { id: 'procurement',             label: 'Procurement',     icon: BarChart3 },
      { id: 'departments',             label: 'Departments',     icon: Building2 },
      // Department Branding Library — every CA department's logo/seal as a
      // deck-ready kit. Public branding, open to every signed-in role; the
      // PANW-side twin lives in GAS only (D-020).
      { id: 'brand-library',           label: 'Dept Branding',   icon: Palette },
      { id: 'zt-mapping',              label: 'ZT Mapping',      icon: Shield,         adminOnly: true },
      { id: 'zt-mapping-eval',         label: 'ZT Mapping Eval', icon: Activity,       adminOnly: true },
    ],
  },
  // Stack Map (codename TechWorld in source/docs) — Phase 0 placeholder.
  // Open to all non-customer roles during dogfood; re-gate in Phase 1
  // once real customer data renders. See TECHWORLD_NORTH_STAR.md /
  // TECHWORLD_DESIGN.md / TECHWORLD_ROADMAP.md.
  {
    id: 'stack-map',
    label: 'Stack Map',
    icon: Globe,
    defaultChild: 'stack-map',
    children: [
      { id: 'stack-map', label: 'Overview', icon: Globe },
      { id: 'stack-map/lab', label: 'Lab', icon: FlaskConical },
    ],
  },
  // Atlas — Cybersecurity portfolio education module.
  // Restricted to admin and palo_user roles only (atlas feature flag).
  // Cisco users and customers do not have atlas enabled by default.
  {
    id: 'atlas',
    label: 'Atlas',
    icon: Compass,
    defaultChild: 'atlas/home',
    children: [
      { id: 'atlas/home',       label: 'Security Map',    icon: MapIcon },
      { id: 'atlas/city',       label: 'Security City',   icon: Building2 },
      { id: 'atlas/cube',       label: '3D Cube',         icon: Box },
      { id: 'atlas/stack',      label: 'Stack Explorer',  icon: Layers },
      { id: 'atlas/products',   label: 'Products',        icon: Package },
      { id: 'atlas/capabilities', label: 'Capabilities',  icon: Table2 },
      { id: 'atlas/maturity',   label: 'Maturity Ladder', icon: Target },
      { id: 'atlas/rosetta',    label: 'Rosetta Stone',   icon: MessageSquare },
      { id: 'atlas/frameworks', label: 'Frameworks',      icon: Shield },
      { id: 'atlas/personas',   label: 'Personas',        icon: Users },
      { id: 'atlas/scenarios',  label: 'Scenarios',       icon: Play },
      { id: 'atlas/glossary',   label: 'Glossary',        icon: BookOpen },
      { id: 'atlas/diagrams',   label: 'Diagrams',        icon: GitBranch },
      { id: 'atlas/workshop',   label: 'Workshop',        icon: Wrench },
      { id: 'atlas/fit-advisor', label: 'Fit Advisor',    icon: Target },
      { id: 'atlas/roadmap',    label: 'Roadmap',         icon: FileOutput },
      { id: 'atlas/learn',      label: 'Learning Paths',  icon: GraduationCap },
      { id: 'atlas/simulators', label: 'Simulators',      icon: Gamepad2 },
      { id: 'atlas/analytics',  label: 'Analytics',       icon: PieChart, adminOnly: true },
    ],
  },
];

export const FEATURE_MAP = {
  sankey: 'spending_flow',
  'classification-hub': 'classification_hub',
  procurement: 'procurement_dashboard',
  departments: 'department_management',
  'tech-stack': 'tech_stack',
  classification: 'classification',
  'zero-trust': 'zero_trust',
  scraper: 'scraper_ops',
  atlas: 'atlas',
  'stack-map': 'stack_map',
  solicitations: 'solicitations',
  'backlog-buster': 'backlog_buster',
  data: 'data_gaps',
  'account-lists': 'account_lists',
  'vendor-analysis': 'vendor_analysis',
  vendors: 'vendor_analysis',
  news: 'news_feed',
  // observability group has no feature flag - always shows, children are admin-gated
  memory: 'memory_dashboard',
};

// Palo user gets a custom nav order focused on their workflow:
// Welcome → Vendor Analysis → My Lists → Spending Flow → Zero Trust
export const PALO_NAV_ORDER = [
  'welcome',
  'vendor-analysis',
  'account-lists',
  'sankey',
  'zero-trust',
];

// Get nav items with role-specific ordering
export function getNavItems(ctx) {
  if (ctx.role === 'palo_user') {
    // Palo users get a FLAT curated rail — look items up across both
    // top-level entries and group children (several PALO_NAV_ORDER ids
    // now live inside the Analysis group).
    const itemMap = new Map();
    for (const item of NAV_ITEMS_BASE) {
      itemMap.set(item.id, item);
      for (const child of item.children || []) itemMap.set(child.id, child);
    }
    return PALO_NAV_ORDER.map(id => itemMap.get(id)).filter(Boolean);
  }

  if (ctx.isCustomer) {
    // Customers only ever see Zero Trust (their sole route) — surface it
    // flat rather than buried in the Analysis group.
    for (const item of NAV_ITEMS_BASE) {
      for (const child of item.children || []) {
        if (child.id === 'zero-trust') return [child];
      }
    }
    return [];
  }

  return NAV_ITEMS_BASE;
}

// Filter an item-list against role/feature gates. Children inherit any
// gate that's not explicitly set on them.
export function filterItems(items, ctx) {
  return items.filter(item => {
    if (ctx.isCustomer) return item.id === 'zero-trust';
    // Palo-only items only show for Palo users
    if (item.paloOnly && ctx.role !== 'palo_user') return false;
    if (item.adminOnly && ctx.role !== 'admin') return false;
    if (item.superAdminOnly && !ctx.isSuperAdmin) return false;
    // Role-specific items (e.g., vendor-analysis for admin + palo_user)
    if (item.roles && !item.roles.includes(ctx.role)) return false;
    const featureKey = FEATURE_MAP[item.id];
    return featureKey ? ctx.isFeatureEnabled(featureKey) : true;
  });
}

/**
 * The role/feature-gated nav tree, groups intact and children gated too.
 *
 * This is the ONE list a user is allowed to customize — Settings →
 * Navigation renders exactly this, so a toggle can never surface a page
 * the gates withheld.
 */
export function getGatedNavTree(ctx) {
  return filterItems(getNavItems(ctx), ctx).map((item) => (
    item.children
      ? { ...item, children: filterItems(item.children, ctx) }
      : item
  ));
}
